import React, { useState, useEffect } from "react";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import { AiOutlineEdit, AiOutlineDelete, AiOutlineUserAdd, AiOutlineSearch } from 'react-icons/ai';
import { toast } from "react-toastify";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import AddUserModal from "./AddUserModal";
import UpdateUserModal from "./UpdateUserModal";
import { api_url } from "../../data";


const UserManagement = () => {
    const { requestWithAuth } = useAuth();
    const [modal, setModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [registeredUsers, setRegisteredUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState("");
    const [registeredMembers, setRegisteredMembers] = useState([]);
    const [searching, setSearching] = useState(false);
    const [totalPages, setTotalPages] = useState(30);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);
    const [pageSize, setPageSize] = useState(10);

    const getRegisteredUsers = async (searchValue, pageSize, page) => {
        setLoadingUsers(true);
        try {
            const response = await requestWithAuth(`/accounts/users/?search=${searchValue}&page_size=${pageSize}&page=${page}`, "GET", { "Content-Type": "application/json" });
            setRegisteredUsers(response?.results || []);
            setNextPage(response?.next);
            setPrevPage(response?.prev);
            setTotalPages(Math.ceil(response?.count / pageSize));
        } catch (error) {
            toast.error("Failed to get users list");
        } finally {
            setLoadingUsers(false);
        }
    };

    const activateEditUserModal = (user) => {
        setSelectedUser(user);
        setEditModal(true);
    };

    const initializeDeleteUser = (userId) => {
        if (window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
            deleteUser(userId);
        }
    };

    const deleteUser = async (userId) => {
        try {
            await requestWithAuth(`/accounts/users/${userId}/`, "DELETE");
            setRegisteredUsers(prev => prev.filter(user => user.id !== userId));
            toast.success("User deleted successfully!");
        } catch (error) {
            toast.error("Failed to delete user");
        }
    };

    // New function to toggle user status
    const handleToggleStatus = async (user) => {
        if(user?.role === "Administrator"){
            return toast.error("You cannot toggle the status of the admin user");
        }
        const newStatus = !user.is_active;
        try {
            
            const response = await requestWithAuth(
                `/accounts/user/${user.id}/`,
                "PUT",
                { is_active: newStatus },
                { "Content-Type": "application/json" },
            );

            setRegisteredUsers(prevUsers =>
                prevUsers.map(u => (u.id === user.id ? { ...u, is_active: newStatus } : u))
            );
            toast.success(`User ${user.username} has been set to ${newStatus ? 'Active' : 'Inactive'}`);
        } catch (error) {
            
            toast.error("Failed to update user status");
        }
    };


    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const totalNumbersToShow = 2;

        let startPage = Math.max(1, currentPage - totalNumbersToShow);
        let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

        if (startPage > 1) {
            pageNumbers.push(1);
            if (startPage > 2) {
                pageNumbers.push("...");
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pageNumbers.push("...");
            }
            pageNumbers.push(totalPages);
        }

        return pageNumbers.map((number, index) => (
            <button
                key={index}
                onClick={() => number !== "..." && handlePageClick(number)}
                disabled={number === "..."}
                className={`
          min-w-[40px] h-10 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors
          ${number === currentPage
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : number === "..."
                            ? "bg-white text-gray-400 border-gray-200 cursor-default"
                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                    }
        `}
            >
                {number}
            </button>
        ));
    };

    const getRoleBadgeColor = (role) => {
        switch (role?.toLowerCase()) {
            case 'administrator':
                return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'finance manager':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'member':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    useEffect(() => {
        getRegisteredUsers(search, pageSize, currentPage);
    }, [search, pageSize, currentPage]);


    return (
        <div className="min-h-screen bg-gray-50/50">
            <AddUserModal show={modal} onClose={() => setModal(false)} />
            <UpdateUserModal 
                show={editModal} 
                user={selectedUser} 
                        onClose={() => {
                            setEditModal(false);
                            setSelectedUser(null);
                        }}
                    />

            <div className="p-6 space-y-6">
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                        <p className="text-gray-600 mt-1">Manage user accounts and permissions</p>
                    </div>
                    <button
                        onClick={() => setModal(true)}
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm hover:shadow-md"
                    >
                        <AiOutlineUserAdd />
                        Add New User
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Users</p>
                                <p className="text-2xl font-bold text-gray-900">{registeredUsers?.length}</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <AiOutlineUserAdd className="text-blue-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Active Users</p>
                                <p className="text-2xl font-bold text-green-600">
                                    {registeredUsers?.filter(user => user.is_active).length}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <div className="w-6 h-6 bg-green-500 rounded-full"></div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Inactive Users</p>
                                <p className="text-2xl font-bold text-red-600">
                                    {registeredUsers.filter(user => !user.is_active).length}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                                <div className="w-6 h-6 bg-red-500 rounded-full"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Users Table Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Card Header */}
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <h2 className="text-lg font-semibold text-gray-900">User Accounts</h2>
                            <div className="relative">
                                <AiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all w-full sm:w-64"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        {loadingUsers ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="flex flex-col items-center gap-3">
                                    <LoadingSpinner />
                                    <p className="text-gray-600 text-sm">Loading users...</p>
                                </div>
                            </div>
                        ) : registeredUsers?.length > 0 ? (
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">User</th>
                                        <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Role</th>
                                        <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Status</th>
                                        <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm hidden md:table-cell">Contact</th>
                                        <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {registeredUsers?.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                                        {user.profile_picture ? (
                                                            <img
                                                                src={`${api_url}${user.profile_picture}`}
                                                                alt="Profile"
                                                                className="w-10 h-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            getInitials(user.first_name, user.last_name)
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="font-medium text-gray-900 truncate">
                                                            {user.first_name} {user.last_name}
                                                        </p>
                                                        <p className="text-sm text-gray-500 truncate">@{user.username}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6">
                                                <label htmlFor={`toggle-${user.id}`} className="flex items-center cursor-pointer">
                                                    <div className="relative">
                                                        <input
                                                            type="checkbox"
                                                            id={`toggle-${user.id}`}
                                                            className="sr-only"
                                                            checked={user.is_active}
                                                            onChange={() => handleToggleStatus(user)}
                                                        />
                                                        <div className={`block w-10 h-6 rounded-full ${user.is_active ? 'bg-green-400' : 'bg-gray-300'}`}></div>
                                                        <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform ${user.is_active ? 'translate-x-full' : ''}`}></div>
                                                    </div>
                                                    <div className={`ml-3 text-sm font-medium ${user.is_active ? 'text-green-700' : 'text-red-700'}`}>
                                                        {user.is_active ? 'Active' : 'Inactive'}
                                                    </div>
                                                </label>
                                            </td>
                                            <td className="py-4 px-6 hidden md:table-cell">
                                                <div className="text-sm">
                                                    <p className="text-gray-900 truncate">{user.email}</p>
                                                    <p className="text-gray-500 truncate">{user.phone_number}</p>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-1">

                                                    <button
                                                        onClick={() => activateEditUserModal(user)}
                                                        className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                                        title="Edit User"
                                                    >
                                                        <AiOutlineEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => initializeDeleteUser(user.id)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete User"
                                                    >
                                                        <AiOutlineDelete />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12 px-6">
                                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <AiOutlineUserAdd className="text-gray-400 w-12 h-12" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {search ? 'No users found' : 'No users yet'}
                                </h3>
                                <p className="text-gray-500 text-center mb-6">
                                    {search
                                        ? `No users match "${search}". Try adjusting your search.`
                                        : 'Get started by adding your first user to the system.'
                                    }
                                </p>
                                {!search && (
                                    <button
                                        onClick={() => setModal(true)}
                                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                                    >
                                        <AiOutlineUserAdd />
                                        Add First User
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {registeredUsers?.length > 0 && totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/30">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <p className="text-sm text-gray-700">
                                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                                    <span className="font-medium">{totalPages}</span>
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handlePreviousPage}
                                        disabled={currentPage === 1}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                      ${currentPage === 1
                                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        Previous
                                    </button>

                                    <div className="flex items-center gap-1">
                                        {renderPageNumbers()}
                                    </div>

                                    <button
                                        onClick={handleNextPage}
                                        disabled={currentPage === totalPages}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                      ${currentPage === totalPages
                                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserManagement;